import React, { useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import LinearProgress from '@material-ui/core/LinearProgress'
import RefreshIcon from '@material-ui/icons/Refresh'
import SettingsIcon from '@material-ui/icons/Settings'
import { changeHeaderName } from '../../actions/dashboardActions'
import { useLangfuseDashboard } from './useLangfuseDashboard'
import { hasCredentials } from './langfuseApi'
import VoiceSettings from './VoiceSettings'
import OverviewTab from './OverviewTab'
import SessionsTab from './SessionsTab'
import AnalyticsTab from './AnalyticsTab'
import ServicesTab from './ServicesTab'
import UsersTab from './UsersTab'
import VoiceChatBubble from './VoiceChatBubble'

var useStyles = makeStyles(function(theme) {
  return {
    appBarSpacer: theme.mixins.toolbar,
    root: { padding: theme.spacing(3) },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    title: { fontWeight: 700, fontSize: 22, color: '#1a2540' },
    lastFetched: { fontSize: 12, color: '#777', marginTop: 2 },
    controls: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' },
    rangeBtn: {
      textTransform: 'none',
      fontWeight: 600,
      minWidth: 48,
      padding: '4px 12px',
      borderRadius: 6,
      border: '1px solid #e0e7ff',
      color: '#6366f1',
    },
    rangeBtnActive: {
      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
      color: '#fff',
      border: 'none',
    },
    refreshBtn: {
      textTransform: 'none',
      fontWeight: 600,
      color: '#6366f1',
      border: '1px solid #e0e7ff',
      borderRadius: 6,
      padding: '4px 12px',
    },
    tabs: {
      backgroundColor: '#fff',
      borderBottom: '1px solid #e0e0e0',
      marginBottom: theme.spacing(2),
    },
    tab: { textTransform: 'none', fontWeight: 600, minWidth: 90 },
    errorBar: {
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.07)',
      border: '1px solid rgba(239,68,68,0.2)',
      borderRadius: 8,
      color: '#c0392b',
      fontSize: 13,
      marginBottom: theme.spacing(2),
    },
    setupCard: {
      padding: theme.spacing(4),
      textAlign: 'center',
      border: '1px solid #e0e7ff',
      borderRadius: 12,
    },
    setupIcon: {
      width: 48,
      height: 48,
      borderRadius: 14,
      background: 'rgba(99,102,241,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
    },
    goSettingsBtn: {
      marginTop: theme.spacing(2),
      textTransform: 'none',
      fontWeight: 600,
      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
      color: '#fff',
      borderRadius: 8,
      '&:hover': { opacity: 0.88, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' },
    },
  }
})

var TAB_LABELS = ['Overview', 'Conversations', 'Analytics', 'Services', 'Users', 'Settings']
var RANGE_OPTIONS = [7, 14, 30, 90]

function makeDateRange(days) {
  var to = new Date()
  var from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from, to: to }
}

function VoiceAnalytics(props) {
  var classes = useStyles()
  var [tab, setTab] = useState(0)
  var [rangeDays, setRangeDays] = useState(7)
  var [credentialsOk, setCredentialsOk] = useState(hasCredentials)
  var [jumpSessionId, setJumpSessionId] = useState(null)

  var dateRange = useMemo(function() { return makeDateRange(rangeDays) }, [rangeDays])
  var dashboard = useLangfuseDashboard(dateRange)

  React.useEffect(function() {
    props.changeHeaderName('Conversational Commerce Analytics')
  }, [])

  // Fetch credentials from MongoDB and seed localStorage. Called on mount and
  // after Settings are saved so MongoDB is always the source of truth.
  function fetchCredentialsFromDB() {
    return fetch('/app/appConfigurations/voice-credentials')
      .then(function(res) { return res.ok ? res.json() : null })
      .then(function(cfg) {
        if (!cfg) return
        if (cfg.langfuseHost) localStorage.setItem('lf_host', cfg.langfuseHost)
        if (cfg.langfusePublicKey) localStorage.setItem('lf_public_key', cfg.langfusePublicKey)
        if (cfg.langfuseSecretKey) localStorage.setItem('lf_secret_key', cfg.langfuseSecretKey)
        if (cfg.openaiApiKey) localStorage.setItem('lf_openai_key', cfg.openaiApiKey)
        window.dispatchEvent(new Event('storage'))
        setCredentialsOk(hasCredentials())
      })
      .catch(function() {})
  }

  React.useEffect(function() {
    fetchCredentialsFromDB()
  }, [])

  // When settings are saved, re-fetch from MongoDB so any cleared fields are
  // restored from the database rather than staying blank.
  function handleSettingsSaved() {
    fetchCredentialsFromDB().then(function() {
      dashboard.refetch()
    })
  }

  function fmtTime(date) {
    if (!date) return ''
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div>
      <div className={classes.appBarSpacer} />
      <div className={classes.root}>
        {/* Header row */}
        {/* <div className={classes.header}>
          <div>
            <Typography className={classes.title}>Voice Analytics</Typography>
            {dashboard.lastFetched && (
              <div className={classes.lastFetched}>Last updated {fmtTime(dashboard.lastFetched)}</div>
            )}
          </div>
          <div className={classes.controls}>
            {RANGE_OPTIONS.map(function(d) {
              return (
                <Button
                  key={d}
                  size="small"
                  className={classes.rangeBtn + (rangeDays === d ? ' ' + classes.rangeBtnActive : '')}
                  onClick={function() { setRangeDays(d) }}
                >
                  {d}d
                </Button>
              )
            })}
            <Button
              size="small"
              className={classes.refreshBtn}
              onClick={dashboard.refetch}
              disabled={dashboard.loading}
              startIcon={dashboard.loading ? <CircularProgress size={13} /> : <RefreshIcon style={{ fontSize: 16 }} />}
            >
              Refresh
            </Button>
          </div>
        </div> */}

        {/* Loading bar */}
        {dashboard.loading && <LinearProgress style={{ marginBottom: 12, borderRadius: 4 }} />}

        {/* Error bar */}
        {dashboard.error && (
          <div className={classes.errorBar}>
            ⚠️ {dashboard.error}
          </div>
        )}

        {/* Tabs */}
        <Paper className={classes.tabs} elevation={0}>
          <Tabs
            value={tab}
            onChange={function(e, v) { setTab(v) }}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {TAB_LABELS.map(function(label) {
              return <Tab key={label} label={label} className={classes.tab} />
            })}
          </Tabs>
        </Paper>

        {/* Tab content */}
        {tab === 5 ? (
          <VoiceSettings onSaved={handleSettingsSaved} />
        ) : !credentialsOk ? (
          <Paper className={classes.setupCard} elevation={0}>
            <div className={classes.setupIcon}>
              <SettingsIcon style={{ color: '#6366f1', fontSize: 22 }} />
            </div>
            <Typography style={{ fontWeight: 700, fontSize: 16, color: '#1a2540' }}>
              Connect Langfuse
            </Typography>
            <Typography style={{ fontSize: 13, color: '#888', marginTop: 8, maxWidth: 380, margin: '8px auto 0' }}>
              Credentials are loaded automatically from the app config. If they haven't been set up yet, add them manually in Settings.
            </Typography>
            <Button
              className={classes.goSettingsBtn}
              variant="contained"
              disableElevation
              onClick={function() { setTab(5) }}
            >
              Go to Settings
            </Button>
          </Paper>
        ) : tab === 0 ? (
          <OverviewTab
            data={dashboard.data}
            loading={dashboard.loading}
            onSelectSession={function(s) { setJumpSessionId(s ? s.id : null); setTab(1) }}
          />
        ) : tab === 2 ? (
          <AnalyticsTab
            data={dashboard.data}
            loading={dashboard.loading}
          />
        ) : tab === 3 ? (
          <ServicesTab
            data={dashboard.data}
            loading={dashboard.loading}
          />
        ) : tab === 4 ? (
          <UsersTab
            data={dashboard.data}
            loading={dashboard.loading}
          />
        ) : tab === 1 ? (
          <SessionsTab
            data={dashboard.data}
            loading={dashboard.loading}
            deletingIds={dashboard.deletingIds}
            initialSessionId={jumpSessionId}
            onDeleteSession={function(s) { dashboard.deleteSession(s) }}
          />
        ) : (
          <Paper style={{ padding: 24, textAlign: 'center', color: '#888', border: '1px solid #e0e0e0', borderRadius: 10 }} elevation={0}>
            <Typography variant="body2">
              {TAB_LABELS[tab]} — building this tab next...
            </Typography>
          </Paper>
        )}
      </div>

      {/* Floating AI chat assistant — visible on all tabs */}
      <VoiceChatBubble data={dashboard.data} rangeDays={rangeDays} />
    </div>
  )
}

var mapDispatchToProps = function(dispatch) {
  return { changeHeaderName: function(name) { dispatch(changeHeaderName(name)) } }
}

export default connect(null, mapDispatchToProps)(VoiceAnalytics)
